/** Shared helpers for admin plan catalog, org assignment, and plan-request pricing. */
import { hasPopulatedPermissionMap } from '../../assets/utils/permissions';

export const PLAN_PRICE_LEVEL_HINTS = {
  L1: 'Retail default — public and individual sales',
  L2: 'Corporate list default — applied when a plan is assigned to an organization',
  L3: 'Per-organization price — negotiated override for one org',
  SME: 'SME list default — applied for small-business organization assignments',
};

/** @deprecated Prefer plan_segments multi-select. Kept for legacy API rows. */
export const PLAN_AUDIENCE = {
  PUBLIC: 'public',
  CORPORATE: 'corporate',
  BOTH: 'both',
  SME: 'sme',
};

export const PLAN_SEGMENTS = {
  RETAIL: 'retail',
  CORPORATE: 'corporate',
  SME: 'sme',
};

export const FALLBACK_SEGMENT_OPTIONS = [
  { label: 'Retail', value: 'retail' },
  { label: 'Corporate', value: 'corporate' },
  { label: 'SME', value: 'sme' },
];

export const PLAN_CATALOG_WRITE_PERMISSIONS = ['admin.*', 'tariffs.manage', 'plans.manage', 'plan_designer.manage'];

export const PLAN_CATALOG_READ_PERMISSIONS = [
  'admin.*',
  'tariffs.manage',
  'tariffs.read_all',
  'tariffs.read',
  'preauth.read',
  'plans.manage',
  'plans.read',
];

export const PLAN_CATEGORY_META = {
  retail: { label: 'Retail', color: 'blue' },
  corporate: { label: 'Corporate', color: 'purple' },
  sme: { label: 'SME', color: 'cyan' },
  utility: { label: 'Utility', color: 'default' },
  archived: { label: 'Archived', color: 'red' },
  active: { label: 'Active', color: 'green' },
  inactive: { label: 'Inactive', color: 'default', hint: 'Legacy disabled plan — not the same as archived' },
};

export const PLAN_SKIP_CODE_LABELS = {
  PLAN_NOT_CORPORATE: 'Not available for organizations',
  PLAN_NOT_PURCHASABLE: 'Utility plan — not assignable',
  PLAN_NOT_ASSIGNED_TO_ORG: 'Not assigned to this organization — use Manage plans first',
  PLAN_SEGMENT_MISMATCH: 'Plan segment does not match this organization\'s enrollment segment',
  PLAN_ARCHIVED: 'Plan is archived',
  PLAN_INACTIVE: 'Plan is inactive',
  PLAN_NOT_FOUND: 'Plan not found',
};

/** Enrollment / deals / staff-upload apply — same codes, enrollment-oriented copy. */
export const PLAN_ENROLLMENT_CODE_LABELS = {
  PLAN_NOT_ASSIGNED_TO_ORG: 'Assign this plan via Manage plans before enrolling members',
  PLAN_NOT_PURCHASABLE: 'Utility plan cannot be used for enrollment on this path',
  PLAN_SEGMENT_MISMATCH: 'This plan\'s segment does not match the organization\'s enrollment segment',
  PLAN_ARCHIVED: 'Plan is archived',
  PLAN_INACTIVE: 'Plan is inactive',
  PLAN_NOT_FOUND: 'Unknown insurance package / plan',
};

const PLAN_ENROLLMENT_CODES = new Set(Object.keys(PLAN_ENROLLMENT_CODE_LABELS));

/** Normalize plan_segments from API row or form value. */
export function normalizePlanSegments(planOrSegments) {
  if (Array.isArray(planOrSegments)) {
    return [...new Set(planOrSegments.map((s) => String(s || '').toLowerCase()).filter(Boolean))];
  }
  const row = planOrSegments || {};
  if (Array.isArray(row.plan_segments) && row.plan_segments.length) {
    return normalizePlanSegments(row.plan_segments);
  }
  if (Array.isArray(row.segments) && row.segments.length) {
    return normalizePlanSegments(row.segments.map((s) => (typeof s === 'string' ? s : s?.key || s?.value)));
  }
  // Legacy exclusive audience → segment set
  const aud = String(row.plan_audience || '').toLowerCase();
  if (aud === 'public') return ['retail'];
  if (aud === 'corporate') return ['corporate'];
  if (aud === 'both') return ['retail', 'corporate'];
  if (aud === 'sme') return ['sme'];
  return [];
}

/** Map taxonomy segment_options[] (fallback to audience_options / static). */
export function taxonomySegmentOptions(taxonomy, fallback = FALLBACK_SEGMENT_OPTIONS) {
  const opts = taxonomy?.segment_options;
  if (Array.isArray(opts) && opts.length) {
    return opts.map((o) => ({
      label: o.label ?? o.key ?? o.value,
      value: String(o.key ?? o.value).toLowerCase(),
    }));
  }
  return fallback;
}

export function planHasSegment(plan, segment) {
  return normalizePlanSegments(plan).includes(String(segment || '').toLowerCase());
}

/** Org effective segment from detail/list enrichment. */
export function getOrgEffectiveSegment(org) {
  return String(
    org?.enrollment_segment
    || org?.enrollment_segment_resolution?.effective_segment
    || org?.org_enrollment_segment_override
    || org?.org_enrollment_segment_locked
    || '',
  ).toLowerCase() || null;
}

/**
 * Plans an organisation may be given under Manage plans: corporate, SME and
 * utility (staff Option A). Archived, inactive, and retail-only plans are out.
 */
export function isAssignableOrgPlan(plan, orgEffectiveSegment = null) {
  if (!plan) return false;
  if (isTruthyPlanFlag(plan.plan_archived)) return false;
  if (plan.plan_status != null && !isTruthyPlanFlag(plan.plan_status)) return false;

  const segments = normalizePlanSegments(plan);
  const isUtility = !isTruthyPlanFlag(plan.plan_purchasable ?? 1);

  // Utility — staff enrollment only (Option A); always assignable when active.
  if (isUtility) return true;

  if (!isTruthyPlanFlag(plan.plan_purchasable ?? 1)
    && !isTruthyPlanFlag(plan.is_corporate_catalog)
    && !isTruthyPlanFlag(plan.is_sme_catalog)
    && !isTruthyPlanFlag(plan.is_public_catalog)
    && !segments.includes('retail')
    && !segments.includes('corporate')
    && !segments.includes('sme')) {
    return false;
  }

  // Retail is the individual-purchase catalog. A member covered under an
  // organisation is enrolled on the organisation's plan, so a retail-only plan
  // can never be the plan on one of their policies — offering it here just
  // produces an assignment that Add Policy cannot honour.
  //
  // A plan carrying BOTH retail and corporate/SME segments is still assignable:
  // it is corporate-capable and happens to also be sold retail. Only
  // retail-ONLY plans are excluded.
  const orgCapable = segments.includes('corporate')
    || segments.includes('sme')
    || isTruthyPlanFlag(plan.is_corporate_catalog)
    || isTruthyPlanFlag(plan.is_sme_catalog);
  if (!orgCapable) return false;

  if (orgEffectiveSegment) {
    const seg = String(orgEffectiveSegment).toLowerCase();
    return segments.includes(seg)
      || (seg === 'corporate' && isTruthyPlanFlag(plan.is_corporate_catalog))
      || (seg === 'sme' && isTruthyPlanFlag(plan.is_sme_catalog));
  }
  return true;
}

/** Label for Manage-plans picker options (utility badge). */
export function assignableOrgPlanLabel(plan) {
  const name = plan?.plan_name || 'Plan';
  if (!isTruthyPlanFlag(plan?.plan_purchasable ?? 1)) {
    return `${name} — utility (staff enrollment only)`;
  }
  return name;
}

export function parsePlanCategories(row) {
  const cats = row?.categories;
  if (Array.isArray(cats) && cats.length) return cats;
  const derived = [];
  if (isTruthyPlanFlag(row?.plan_archived)) derived.push('archived');
  else derived.push(isTruthyPlanFlag(row?.plan_status) || row?.plan_status == null ? 'active' : 'inactive');
  if (!isTruthyPlanFlag(row?.plan_purchasable)) derived.push('utility');
  normalizePlanSegments(row).forEach((s) => {
    if (s === 'retail' || s === 'corporate' || s === 'sme') derived.push(s);
  });
  return [...new Set(derived)];
}

/** Which price field groups to show for a segment set (or legacy audience string). */
export function planPriceVisibilityForSegments(segmentsOrAudience) {
  if (typeof segmentsOrAudience === 'string') {
    return planPriceVisibilityForAudience(segmentsOrAudience);
  }
  const segments = normalizePlanSegments(segmentsOrAudience);
  return {
    retail: segments.includes('retail'),
    corporate: segments.includes('corporate'),
    sme: segments.includes('sme'),
  };
}

/** @deprecated Use planPriceVisibilityForSegments */
export function planPriceVisibilityForAudience(audience) {
  const aud = String(audience || '').toLowerCase();
  return {
    retail: aud === PLAN_AUDIENCE.PUBLIC || aud === PLAN_AUDIENCE.BOTH,
    corporate: aud === PLAN_AUDIENCE.CORPORATE || aud === PLAN_AUDIENCE.BOTH,
    sme: aud === PLAN_AUDIENCE.SME,
  };
}

export function planPriceFallbackHint(segmentsOrAudience) {
  const vis = planPriceVisibilityForSegments(segmentsOrAudience);
  const parts = [];
  if (vis.corporate && vis.retail) parts.push('corporate → retail');
  if (vis.sme && vis.retail) parts.push('SME → retail');
  if (parts.length) {
    return `Leave a price blank to fall back to the next level (${parts.join('; ')}).`;
  }
  return 'Leave a price blank to use system defaults where applicable.';
}

export function buildPlanCatalogPricePayload(segmentsOrAudience, values = {}) {
  const vis = planPriceVisibilityForSegments(segmentsOrAudience);
  const payload = {};
  if (vis.retail) {
    payload.plan_cost = values.plan_cost ?? null;
    payload.plan_six_cost = values.plan_six_cost ?? null;
  }
  if (vis.corporate) {
    payload.plan_corporate_cost = values.plan_corporate_cost ?? null;
    payload.plan_corporate_family_cost = values.plan_corporate_family_cost ?? null;
  }
  if (vis.sme) {
    payload.plan_sme_cost = values.plan_sme_cost ?? null;
    payload.plan_sme_family_cost = values.plan_sme_family_cost ?? null;
  }
  return payload;
}

export function audienceShowsRetailPrice(planOrAudience) {
  if (planOrAudience && typeof planOrAudience === 'object') {
    return planPriceVisibilityForSegments(planOrAudience).retail;
  }
  return planPriceVisibilityForAudience(planOrAudience).retail;
}

export function audienceShowsCorporatePrice(planOrAudience) {
  if (planOrAudience && typeof planOrAudience === 'object') {
    return planPriceVisibilityForSegments(planOrAudience).corporate;
  }
  return planPriceVisibilityForAudience(planOrAudience).corporate;
}

export function audienceShowsSmePrice(planOrAudience) {
  if (planOrAudience && typeof planOrAudience === 'object') {
    return planPriceVisibilityForSegments(planOrAudience).sme;
  }
  return planPriceVisibilityForAudience(planOrAudience).sme;
}

export function defaultOrgPlanCosts(plan, orgEffectiveSegment = null) {
  const segment = String(orgEffectiveSegment || '').toLowerCase();
  const preferSme = segment === 'sme' || planHasSegment(plan, 'sme');
  if (preferSme && segment === 'sme') {
    return {
      cost: plan?.plan_sme_cost != null && plan?.plan_sme_cost !== ''
        ? Number(plan.plan_sme_cost)
        : (plan?.plan_corporate_cost != null && plan?.plan_corporate_cost !== ''
          ? Number(plan.plan_corporate_cost)
          : (plan?.plan_cost != null && plan?.plan_cost !== '' ? Number(plan.plan_cost) : undefined)),
      family_cost: plan?.plan_sme_family_cost != null && plan?.plan_sme_family_cost !== ''
        ? Number(plan.plan_sme_family_cost)
        : (plan?.plan_corporate_family_cost != null && plan?.plan_corporate_family_cost !== ''
          ? Number(plan.plan_corporate_family_cost)
          : (plan?.plan_six_cost != null && plan?.plan_six_cost !== '' ? Number(plan.plan_six_cost) : undefined)),
    };
  }
  return {
    cost: plan?.plan_corporate_cost != null && plan?.plan_corporate_cost !== ''
      ? Number(plan.plan_corporate_cost)
      : (plan?.plan_cost != null && plan?.plan_cost !== '' ? Number(plan.plan_cost) : undefined),
    family_cost: plan?.plan_corporate_family_cost != null && plan?.plan_corporate_family_cost !== ''
      ? Number(plan.plan_corporate_family_cost)
      : (plan?.plan_six_cost != null && plan?.plan_six_cost !== '' ? Number(plan.plan_six_cost) : undefined),
  };
}

/** Pull `[PLAN_NOT_ASSIGNED_TO_ORG]` (etc.) from CSV / row error strings. */
export function extractBracketedPlanCode(text) {
  const raw = String(text || '');
  const bracketed = raw.match(/\[(PLAN_[A-Z0-9_]+)\]/);
  if (bracketed && PLAN_ENROLLMENT_CODES.has(bracketed[1])) return bracketed[1];
  const bare = raw.match(/\b(PLAN_[A-Z0-9_]+)\b/);
  if (bare && PLAN_ENROLLMENT_CODES.has(bare[1])) return bare[1];
  return null;
}

export function stripBracketedPlanCode(text) {
  return String(text || '')
    .replace(/\s*\[PLAN_[A-Z0-9_]+\]\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalize plan enrollment failures from API envelopes, axios errors, or plain strings.
 * @returns {{ code: string|null, message: string, plan_id?: *, plan_name?: *, social_no?: *, hint: string|null }}
 */
export function extractPlanEnrollmentError(source) {
  if (source == null) {
    return { code: null, message: '', hint: null };
  }
  if (typeof source === 'string') {
    const code = extractBracketedPlanCode(source);
    const message = stripBracketedPlanCode(source) || source;
    return {
      code,
      message,
      hint: code ? PLAN_ENROLLMENT_CODE_LABELS[code] : null,
    };
  }

  const body = source?.response?.data
    ?? source?.body
    ?? source;
  const data = (body?.data && typeof body.data === 'object' && !Array.isArray(body.data))
    ? body.data
    : {};
  const rawMessage = String(
    data.message
    || body?.message
    || body?.msg
    || source?.message
    || '',
  ).trim();
  const rawCode = data.code
    || body?.code
    || source?.code
    || extractBracketedPlanCode(rawMessage);
  const code = PLAN_ENROLLMENT_CODES.has(rawCode) ? rawCode : null;
  const message = stripBracketedPlanCode(rawMessage) || rawMessage;

  return {
    code,
    message,
    plan_id: data.plan_id ?? null,
    plan_name: data.plan_name ?? null,
    social_no: data.social_no ?? null,
    hint: code ? PLAN_ENROLLMENT_CODE_LABELS[code] : null,
  };
}

/** Toast / alert description for create & upload failures. */
export function formatPlanEnrollmentErrorMessage(source, { fallback = 'Request failed' } = {}) {
  const err = extractPlanEnrollmentError(source);
  if (err.message) {
    if (err.code && !err.message.includes(err.code)) {
      return `${err.message} [${err.code}]`;
    }
    return err.message;
  }
  if (err.hint) {
    return err.code ? `${err.hint} [${err.code}]` : err.hint;
  }
  return fallback;
}

/** Format a single CSV / staff-upload row error for display. */
export function formatPlanEnrollmentRowError(errText) {
  const err = extractPlanEnrollmentError(errText);
  const text = err.message || String(errText || 'Unknown error');
  return {
    code: err.code,
    text,
    hint: err.hint,
    display: err.code && !text.includes(err.code) ? `${text} [${err.code}]` : text,
  };
}

const LEGACY_PLAN_ROLES = [1, 5];

export function canManagePlanCatalog(user, hasAnyPermission) {
  if (typeof hasAnyPermission === 'function' && hasAnyPermission(PLAN_CATALOG_WRITE_PERMISSIONS)) {
    return true;
  }
  // Legacy staff role ids apply only while the session has no RBAC permission map.
  if (hasPopulatedPermissionMap(null, user)) return false;
  return LEGACY_PLAN_ROLES.includes(Number(user?.role?.staffRoleId));
}

export function isTruthyPlanFlag(v) {
  return v === true || v === 1 || v === '1';
}

export function formatPlanReferencePrices(referencePrices) {
  if (!referencePrices || typeof referencePrices !== 'object') return null;
  return {
    retail_unit: referencePrices.retail_unit ?? referencePrices.plan_cost ?? referencePrices.l1_unit ?? null,
    retail_family: referencePrices.retail_family ?? referencePrices.plan_six_cost ?? referencePrices.l1_family ?? null,
    corporate_unit: referencePrices.corporate_unit ?? referencePrices.plan_corporate_cost ?? referencePrices.l2_unit ?? null,
    corporate_family: referencePrices.corporate_family ?? referencePrices.plan_corporate_family_cost ?? referencePrices.l2_family ?? null,
    sme_unit: referencePrices.sme_unit ?? referencePrices.plan_sme_cost ?? null,
    sme_family: referencePrices.sme_family ?? referencePrices.plan_sme_family_cost ?? null,
  };
}

export function formatPlanMoney(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** InputNumber props that stay stable when the user deletes the current value (antd passes null). */
export const PLAN_MONEY_INPUT_PROPS = {
  min: 0,
  formatter: (v) => (v == null || v === '' ? '' : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')),
  parser: (v) => (v ? String(v).replace(/[₦,\s]/g, '') : ''),
};

export function extractPlanAddSkipped(parsed) {
  if (!parsed) return [];
  const data = parsed.data ?? parsed.body?.data ?? {};
  const skipped = data.skipped ?? parsed.body?.skipped;
  if (Array.isArray(skipped) && skipped.length) return skipped;
  const code = parsed.code || data.code || parsed.body?.code;
  if (code && PLAN_SKIP_CODE_LABELS[code]) {
    return [{ plan_id: data.plan_id, code, reason: parsed.message || PLAN_SKIP_CODE_LABELS[code] }];
  }
  return [];
}

export function describePlanSkip(item) {
  const code = item?.code || item?.reason_code;
  return item?.reason || item?.message || PLAN_SKIP_CODE_LABELS[code] || code || 'Could not assign plan';
}

/** Scope tabs we hide from the segmented control (still visible as row category chips). */
export const PLAN_CATALOG_HIDDEN_SCOPE_TABS = new Set(['inactive']);

/**
 * Resolve GET /v2/admin/plans query params from a taxonomy scope entry.
 * Backend sends scopes[].query as an object (e.g. { scope: 'public' }), not a string.
 */
export function resolveTaxonomyScopeQuery(scopeEntry) {
  const q = scopeEntry?.query;
  if (q && typeof q === 'object' && !Array.isArray(q)) {
    return { ...q };
  }
  if (typeof q === 'string' && q.trim()) {
    return { scope: q.trim() };
  }
  const key = scopeEntry?.key ?? scopeEntry?.value;
  if (key) return { scope: String(key) };
  return { scope: 'all' };
}

/** Map taxonomy scopes[] to Segmented options (value + queryParams for list fetch). */
export function taxonomyScopeOptions(taxonomy) {
  const scopes = taxonomy?.scopes;
  if (!Array.isArray(scopes) || !scopes.length) return null;
  return scopes
    .filter((s) => {
      const tabKey = String(s.key ?? s.value ?? '').toLowerCase();
      return tabKey && !PLAN_CATALOG_HIDDEN_SCOPE_TABS.has(tabKey);
    })
    .map((s) => {
      const value = s.key ?? s.value ?? resolveTaxonomyScopeQuery(s).scope ?? 'all';
      const queryParams = resolveTaxonomyScopeQuery(s);
      const count = s.count != null ? Number(s.count) : null;
      const label = count != null && count > 0 ? `${s.label} (${count})` : (s.label || value);
      return {
        label,
        value,
        queryParams,
        overlaps: Boolean(s.overlaps),
        description: s.description || null,
      };
    });
}

/** Map taxonomy audience_options[] to Select options (`key` is the API value). */
export function taxonomyAudienceOptions(taxonomy, fallback = []) {
  const opts = taxonomy?.audience_options;
  if (!Array.isArray(opts) || !opts.length) return fallback;
  return opts.map((o) => ({
    label: o.label ?? o.key ?? o.value,
    value: o.key ?? o.value,
  }));
}

/** Lookup a taxonomy flag definition (e.g. plan_purchasable labels for edit form). */
export function taxonomyFlagMeta(taxonomy, flagKey) {
  const flags = taxonomy?.flags;
  if (!Array.isArray(flags) || !flagKey) return null;
  return flags.find((f) => f.key === flagKey) ?? null;
}
